import type { MailboxInfo } from "owa-client-types";
import { getMailboxTypeForBizchatTelemetry } from "owa-bizchat-account-utils";
import { OKRCategories } from "./types/OKRCategories";
import type { PrioritizeAction, PrioritizeTelemetryEvent, PrioritizeTelemetryEntryPoint, } from "./types/scenarios/Prioritize";
import { type EventArea, logCoreUsage } from "owa-analytics";

function getOKRCategoryForEvent(event: PrioritizeTelemetryEvent): OKRCategories {
    switch (event) {
        case "OpenInbox":
        case "OpenSort":
        case "OpenFilter":
            return OKRCategories.Seen;
        case "EnabledChanged":
        case "RulesCountChanged":
        case "SummaryCardExpanded":
        case "PrioritizeSummary_Clicked":
            return OKRCategories.Tried;
        case "SummaryCardContentCopied":
            return OKRCategories.Kept;
        default:
            return OKRCategories.None;
    }
}

function getEntryPointForEvent(event: PrioritizeTelemetryEvent): PrioritizeTelemetryEntryPoint | undefined {
    switch (event) {
        case "OpenInbox":
        case "OpenSort":
        case "OpenFilter":
            return "MessageList";
        case "EnabledChanged":
        case "RulesCountChanged":
            return "Settings";
        case "SummaryCardExpanded":
        case "SummaryCardContentCopied":
        case "PrioritizeSummary_Clicked":
            return "ReadingPane";
        default:
            return undefined;
    }
}

function getActionForEvent(event: PrioritizeTelemetryEvent): PrioritizeAction {
    switch (event) {
        case "EnabledChanged":
        case "RulesCountChanged":
            return "SettingChange";
        case "PrioritizeSummary_Clicked":
        case "SummaryCardExpanded":
            return "Expand";
        case "SummaryCardContentCopied":
            return "Copy";
        default:
            return "Other";
    }
}

function getVariantDataForEvent(event: PrioritizeTelemetryEvent): {
    [key: string]: string;
} {
    switch (event) {
        case "EnabledChanged":
            return { SettingName: "Enabled" };
        case "RulesCountChanged":
            return { SettingName: "RulesCount" };
        case "OpenFilter":
            return { NavigationTarget: "Filter" };
        case "OpenInbox":
            return { NavigationTarget: "Inbox" };
        case "OpenSort":
            return { NavigationTarget: "Sort" };
        case "PrioritizeSummary_Clicked":
            return { SummaryAction: "Clicked" };
        case "SummaryCardContentCopied":
            return { SummaryAction: "Copied" };
        case "SummaryCardExpanded":
            return { SummaryAction: "Expanded" };
        default:
            return {};
    }
}

export function logPrioritizeTelemetryEvent(event: PrioritizeTelemetryEvent, mailboxInfo: MailboxInfo, customData: {
    [key: string]: any;
} = {}): void {
    const canonicalTelemetryData = {
        OKRCategory: getOKRCategoryForEvent(event),
        EntryPoint: getEntryPointForEvent(event),
        Action: getActionForEvent(event),
        Scenario: "Prioritize",
        MailboxProvider: mailboxInfo.mailboxProvider,
        MailboxType: getMailboxTypeForBizchatTelemetry(mailboxInfo),
    };
    const distinctTelemetryData = {
        ...customData,
        ...canonicalTelemetryData,
        ...getVariantDataForEvent(event),
    };
    logCoreUsage("AIStandardTelemetry_Prioritize", mailboxInfo, distinctTelemetryData, {
        isScorecardEvent: true,
        eventArea: "AI",
    });
}
